import { FC, useEffect, useState } from 'react';

import { Button } from '@/shared/uikitV2/Button';
import { Checkbox } from '@/shared/uikitV2/Checkbox';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { collectionKind } from '@/widgets/ExtractFormData/helpers';
import { FileUploader, UploadState } from '@/widgets/FileUploader';

import { BtnClose } from '../../components/BtnClose';
import { useModalsStore } from '../../modals.store';
import { ExtractModalProps } from '../../types';

import { ExtractBanner } from './ExtractBanner';
import { Actions, Body, Checkboxes, Container, Header, StyledDrawer, Title } from './styled';

export const ExtractModal: FC<ExtractModalProps> = ({
  entityType,
  extractFiles,
  addFiles,
  subtype,
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'extractModal' });
  const [isExtract, setIsExtract] = useState(false);
  const [isAttach, setIsAttach] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const modals = useModalsStore();
  const [uploadState, setUploadState] = useState<UploadState | null>();
  const isAsset = entityType === 'assets';

  const files = uploadState?.files;
  const isEmptyList = (files?.length ?? 0) === 0;

  const onUserClose = () => modals.closeModal();
  const onUpload = async () => {
    if (isAsset || isExtract) {
      if (!files) return;
      setIsLoading(true);

      await extractFiles({
        files: files.map((item) => item.file),
        kind: collectionKind[entityType],
        subtype,
      });
      setIsLoading(false);
    }

    if (isAttach) {
      if (!files) return;
      addFiles?.(files.map((item) => item.file));
    }

    modals.closeModal();
  };

  useEffect(() => {
    const shouldPrevent = Boolean(files?.length);
    if (!shouldPrevent) return undefined;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [files?.length]);

  return (
    <StyledDrawer width={600} footer={null} closable={false} onClose={onUserClose} open>
      <Container>
        <Header>
          <Title className="truncate" size={22} bold>
            {t('title')}
          </Title>
          <BtnClose onClick={onUserClose} style={{ position: 'absolute', right: 24, top: 24 }} />
        </Header>
        <Body style={{ marginRight: 4 }} className="uikitScrollbar">
          <ExtractBanner />

          {!isAsset && (
            <Checkboxes>
              <Checkbox
                checked={isExtract}
                onChange={(e) => setIsExtract(e.target.checked)}
                label={t('extract')}
              />
              <Checkbox
                checked={isAttach}
                onChange={(e) => setIsAttach(e.target.checked)}
                label={t('attach')}
              />
            </Checkboxes>
          )}

          <FileUploader onUpdate={setUploadState} />
        </Body>
        <Actions>
          <Button type="text" onClick={onUserClose} style={{ marginRight: 'auto' }}>
            {t('btnCancel')}
          </Button>
          <Button
            style={{ minWidth: 200 }}
            loading={isLoading}
            disabled={isAsset ? isEmptyList : !(isEmptyList === false && (isExtract || isAttach))}
            onClick={onUpload}
          >
            {t('btnConfirm')}
          </Button>
        </Actions>
      </Container>
    </StyledDrawer>
  );
};
